import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import labPlugin from 'colord/plugins/lab';
import mixPlugin from 'colord/plugins/mix';
extend([
    a11yPlugin,
    labPlugin,
    mixPlugin
]);
export const WCAG_AA_MIN = 4.5;
export const DARK_900 = '#282863';
export const DARK_700 = '#696992';
export const DARK_50 = '#fcfcfd';
export const DARK_200 = '#d4d4e0';
const DARK_300 = '#bfbfd0';
const DARK_600 = '#7e7ea1';
export function getContrastRatio(bgHex, textHex) {
    const bg = colord(bgHex);
    const text = colord(textHex);
    if (!bg.isValid() || !text.isValid()) return 1;
    return bg.contrast(text);
}
export function getTextColorModeForBackground(bgHex) {
    const c = colord(bgHex);
    if (!c.isValid()) return 'dark';
    const contrastLight = c.contrast(DARK_50);
    const contrastDark = c.contrast(DARK_900);
    if (contrastDark >= WCAG_AA_MIN) return 'dark';
    if (contrastLight >= WCAG_AA_MIN) return 'light';
    return 'dark';
}
export function getTextColorsForBackground(bgHex) {
    const mode = getTextColorModeForBackground(bgHex);
    return mode === 'dark' ? {
        primary: DARK_900,
        secondary: DARK_700
    } : {
        primary: DARK_50,
        secondary: DARK_200
    };
}
export function getDisabledTextColorForBackground(bgHex) {
    const c = colord(bgHex);
    if (!c.isValid()) return DARK_600;
    const contrast600 = getContrastRatio(bgHex, DARK_600);
    const contrast300 = getContrastRatio(bgHex, DARK_300);
    return contrast300 >= contrast600 ? DARK_300 : DARK_600;
}
const WHITE = '#ffffff';
/**
 * Returns the hex of color-mix(in srgb, brandColor mixPercent%, white).
 * E.g. mixPercent=15 yields 15% brand + 85% white (matches CSS color-mix 15%).
 */ export function getMixedColorHex(brandHex, mixPercent) {
    const brand = colord(brandHex);
    if (!brand.isValid()) return WHITE;
    const ratio = mixPercent / 100;
    return brand.mix(WHITE, 1 - ratio).toHex();
}
/**
 * For elements that use brand color as text/foreground on a given background:
 * if brand color has sufficient contrast, use it; else use deterministic fallback
 * (Dark/900 or Dark/50 per getTextColorsForBackground).
 */ export function getTextColorForBrandOnBackground(bgHex, brandHex) {
    if (!colord(bgHex).isValid() || !colord(brandHex).isValid()) return DARK_900;
    if (getContrastRatio(bgHex, brandHex) >= WCAG_AA_MIN) return brandHex;
    return getTextColorsForBackground(bgHex).primary;
}
/**
 * Brand color for text/CTAs on a background. Prefers brand when contrast ≥ 4.5.
 * When brand fails, tries darkening (on light bg) or lightening (on dark bg) to preserve hue
 * while meeting WCAG AA. Falls back to Dark/900 or Dark/50 if adjustment fails.
 */ export function getBrandColorWithContrast(bgHex, brandHex) {
    if (!colord(bgHex).isValid() || !colord(brandHex).isValid()) return DARK_900;
    if (getContrastRatio(bgHex, brandHex) >= WCAG_AA_MIN) return brandHex;
    const bg = colord(bgHex);
    const isBgLight = bg.toHsl().l > 0.5;
    const step = 0.08;
    const maxIterations = 12;
    let brand = colord(brandHex);
    for(let i = 0; i < maxIterations; i++){
        const hex = brand.toHex();
        if (getContrastRatio(bgHex, hex) >= WCAG_AA_MIN) return hex;
        brand = isBgLight ? brand.darken(step) : brand.lighten(step);
    }
    return getTextColorsForBackground(bgHex).primary;
}
/**
 * Hover color for text buttons using --text-button-on-secondary-bg.
 * When base is brand → darker brand; when base is fallback → slightly darker/lighter for feedback.
 */ export function getTextButtonOnSecondaryBgHover(baseTextColor, brandHex) {
    if (!colord(baseTextColor).isValid() || !colord(brandHex).isValid()) return DARK_900;
    const base = colord(baseTextColor);
    const brand = colord(brandHex);
    if (base.isEqual(brand)) return brand.darken(0.1).toHex();
    return base.isDark() ? base.darken(0.1).toHex() : base.lighten(0.05).toHex();
}
/**
 * Text color for badges/buttons with background = color-mix(brand 15%, white).
 * Same deterministic fallback as primary text: Dark/900 or Dark/50 per contrast.
 */ export function getTextColorForTintedBrandBackground(brandHex) {
    const tintedBg = getMixedColorHex(brandHex, 15);
    return getTextColorsForBackground(tintedBg).primary;
}
const LIGHTNESS_MIN = 0.08;
const LIGHTNESS_MAX = 0.92;
export function adjustBackgroundForContrast(bgHex) {
    let c = colord(bgHex);
    if (!c.isValid()) return bgHex;
    const step = 0.07;
    const maxIterations = 15;
    for(let i = 0; i < maxIterations; i++){
        const hex = c.toHex();
        if (getContrastRatio(hex, DARK_900) >= WCAG_AA_MIN) return hex;
        if (getContrastRatio(hex, DARK_50) >= WCAG_AA_MIN) return hex;
        const hsl = c.toHsl();
        hsl.s = Math.min(0.8, hsl.s);
        hsl.l = hsl.l > 0.5 ? Math.min(LIGHTNESS_MAX, hsl.l + step) : Math.max(LIGHTNESS_MIN, hsl.l - step);
        c = colord({
            h: hsl.h,
            s: hsl.s,
            l: hsl.l,
            a: hsl.a
        });
    }
    return c.toHex();
}
const GRADIENT_DELTA_L_MIN = 8;
export function validateAndCorrectGradient(startHex, endHex) {
    let start = colord(startHex).isValid() ? startHex : DARK_900;
    let end = colord(endHex).isValid() ? endHex : DARK_900;
    const checkStops = (s, e)=>{
        const startDark = getContrastRatio(s, DARK_900) >= WCAG_AA_MIN;
        const startLight = getContrastRatio(s, DARK_50) >= WCAG_AA_MIN;
        const endDark = getContrastRatio(e, DARK_900) >= WCAG_AA_MIN;
        const endLight = getContrastRatio(e, DARK_50) >= WCAG_AA_MIN;
        if (startDark && endDark) return {
            useDark: true,
            needsCorrection: false
        };
        if (startLight && endLight) return {
            useDark: false,
            needsCorrection: false
        };
        return {
            useDark: true,
            needsCorrection: true
        };
    };
    let result = checkStops(start, end);
    if (result.needsCorrection) {
        start = adjustBackgroundForContrast(start);
        end = adjustBackgroundForContrast(end);
        result = checkStops(start, end);
    }
    const startLab = colord(start).toLab();
    const endLab = colord(end).toLab();
    const deltaL = Math.abs(startLab.l - endLab.l);
    if (deltaL < GRADIENT_DELTA_L_MIN) {
        var _endLab_alpha;
        const targetDelta = GRADIENT_DELTA_L_MIN - deltaL;
        const adjustEnd = endLab.l > startLab.l;
        const newEndL = adjustEnd ? Math.min(92, endLab.l + targetDelta) : Math.max(8, endLab.l - targetDelta);
        const labColor = {
            l: newEndL,
            a: endLab.a,
            b: endLab.b,
            alpha: (_endLab_alpha = endLab.alpha) !== null && _endLab_alpha !== void 0 ? _endLab_alpha : 1
        };
        end = colord(labColor).toHex();
    }
    const textPrimary = result.useDark ? DARK_900 : DARK_50;
    const textSecondary = result.useDark ? DARK_700 : DARK_200;
    return {
        startHex: start,
        endHex: end,
        textPrimary,
        textSecondary
    };
}
